import { ReactNode } from 'react';

import capitalize from 'lodash/capitalize';
import round from 'lodash/round';

import {
  EllipsisWithTooltip,
  ExternalCell,
  ExternalRow,
  Icons,
  InfoTooltip,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { WorkloadEventType } from '@cast/types';

import { CopyToClipboard } from 'components/clickable-actions';

import { ConfigurationEventSubRow } from './ConfigurationEventSubRow';
import { CPUPressureEventSubRow } from './CPUPressureEventSubRow';
import { CPUPressureResolvedEventSubRow } from './CPUPressureResolvedEventSubRow';
import { DetailsRowContainer, DetailWitTitle } from './DetailsRowContainer';
import { FailedHelmTestEventSubRow } from './FailedHelmTestEventSubRow';
import { ScalingPolicyEventDetailsRowContainer } from './ScalingPolicyEventDetailsRowContainer';
import { ScalingPolicyOrderChangedEventDetailsRow } from './ScalingPolicyOrderChangedEventDetailsRow';
import { ScalingPolicyUpdatedEventSubRow } from './ScalingPolicyUpdatedEventSubRow';
import { StartupFailureEventRow } from './StartupFailureEventRow';
import { SystemOverrideResetEventRow } from './SystemOverrideResetEventRow';
import { SystemOverrideTriggeredEventRow } from './SystemOverrideTriggeredEventRow';
import {
  OOMKillEvent,
  PreparedEvent,
  RecommendedPodCountChangedEvent,
  RecommendedRequestsChangedEvent,
  SurgeEvent,
} from '../../../types';

const RecommendationEvent = (event: RecommendedRequestsChangedEvent) => {
  const rows = event.containerNames.map((name) => {
    const prev = event.details.previous.containers?.find(
      ({ name: cName }) => cName === name
    );
    const curr = event.details.current.containers?.find(
      ({ name: cName }) => cName === name
    );
    const numOrNothing = (num?: number | null) =>
      num != null ? round(num, 3) : undefined;
    return {
      name,
      cpu: {
        prev: numOrNothing(prev?.resources?.requests?.cpuCores),
        curr: numOrNothing(curr?.resources?.requests?.cpuCores),
      },
      memory: {
        prev: numOrNothing(prev?.resources?.requests?.memoryGib),
        curr: numOrNothing(curr?.resources?.requests?.memoryGib),
      },
    };
  });

  const renderReqToRec = (prev?: number, curr?: number, unit?: string) => {
    return (
      <Stack direction="row" alignItems="center" gap={8}>
        <Typography
          variant="P12R"
          color={prev === undefined ? 'textSecondary' : undefined}
        >
          {prev ?? 'N/A'}
        </Typography>
        <Icons.ArrowRight
          css={({ palette }) => ({ color: palette.grey[400] })}
        />
        <Typography
          variant="P12R"
          color={curr === undefined ? 'textSecondary' : undefined}
        >
          {curr ?? 'N/A'}
        </Typography>
        {unit && (
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            {unit}
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <DetailsRowContainer event={event}>
      <ExternalRow
        height={22}
        top={-12}
        sx={{ borderTopColor: 'transparent !important' }}
      >
        <ExternalCell startColumn={0}>
          <DetailWitTitle
            title="Autoscaler mode:"
            detail={capitalize(event.details.applyType)}
            titleVariant="light"
          />
        </ExternalCell>
      </ExternalRow>
      {!!rows.length && (
        <>
          <ExternalRow typography="L10B" height={36}>
            <ExternalCell>Container</ExternalCell>
            <ExternalCell startColumn={1} whiteSpace="nowrap">
              CPU REQ TO RECOMMENDED
            </ExternalCell>
            <ExternalCell
              startColumn={2}
              whiteSpace="nowrap"
              textTransform="initial"
            >
              GiB REQ TO RECOMMENDED
            </ExternalCell>
          </ExternalRow>
          {rows.map(({ name, cpu, memory }) => (
            <ExternalRow key={name} typography="P12R" height={28}>
              <ExternalCell startColumn={0}>
                <EllipsisWithTooltip
                  TypographyProps={{
                    pr: 16,
                    variant: 'inherit',
                  }}
                >
                  {name}
                </EllipsisWithTooltip>
              </ExternalCell>
              <ExternalCell startColumn={1}>
                {renderReqToRec(cpu.prev, cpu.curr, 'CPU')}
              </ExternalCell>
              <ExternalCell startColumn={2}>
                {renderReqToRec(memory.prev, memory.curr, 'GiB')}
              </ExternalCell>
            </ExternalRow>
          ))}
        </>
      )}
    </DetailsRowContainer>
  );
};

const OomKill = (event: OOMKillEvent) => {
  return (
    <DetailsRowContainer event={event}>
      {event.containerNames.length && (
        <>
          <ExternalRow
            height={36}
            typography="L10B"
            color="grey.700"
            sx={{ borderTop: tokens.theme.border.structural.neutral }}
          >
            <ExternalCell>Containers</ExternalCell>
          </ExternalRow>
          {event.details.containers.map(({ name }) => (
            <ExternalRow key={name} height={28}>
              <ExternalCell startColumn={0} cellProps={{ typography: 'P12R' }}>
                <Stack direction="row" gap={4} alignItems="center">
                  <CopyToClipboard text={name} iconSize={16} />
                  {name}
                </Stack>
              </ExternalCell>
            </ExternalRow>
          ))}
        </>
      )}
    </DetailsRowContainer>
  );
};

const RecommendedPodCountChanged = (event: RecommendedPodCountChangedEvent) => {
  const { previous, current } = event.details;
  return (
    <DetailsRowContainer
      event={event}
      titleRowProps={{
        sx: {
          height: 60,
          py: 12,
          alignItems: 'flex-start',
        },
      }}
      titleRowContent={
        <ExternalCell startColumn={1}>
          <Stack>
            <Typography variant="L10M">POD COUNT:</Typography>
            <Stack
              direction="row"
              alignItems="center"
              gap={8}
              borderLeft="1px solid"
              borderColor={tokens.theme.border.structural.neutral}
              pl={8}
              mt={4}
            >
              <Typography
                variant="P10R"
                color={previous === undefined ? 'textSecondary' : undefined}
              >
                {previous ?? 'N/A'}
              </Typography>
              <Icons.ArrowRight
                css={({ palette }) => ({ color: palette.grey[400] })}
              />
              <Typography
                variant="P10R"
                color={current === undefined ? 'textSecondary' : undefined}
              >
                {current ?? 'N/A'}
              </Typography>
            </Stack>
          </Stack>
        </ExternalCell>
      }
    />
  );
};

const Surge = (event: SurgeEvent) => {
  const renderSurge = (
    prev: number | null | undefined,
    curr: number | null | undefined,
    acronym: string
  ) => {
    if (prev === curr) {
      return null;
    }
    return (
      <Stack direction="row" alignItems="center" gap={8}>
        <Stack direction="row" alignItems="center">
          <Typography variant="P12R" mr={4}>
            {prev ?? '-'}
          </Typography>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            {acronym}
          </Typography>
        </Stack>
        <Icons.ArrowUpRight
          css={({ palette }) => ({ color: palette.grey[500] })}
        />
        <Stack direction="row" alignItems="center">
          <Typography variant="P12R" mr={4}>
            {curr ?? '-'}
          </Typography>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            {acronym}
          </Typography>
        </Stack>
      </Stack>
    );
  };
  return (
    <DetailsRowContainer event={event}>
      {!!event.details.containers.length && (
        <>
          <ExternalRow height={36} typography="L10B">
            <ExternalCell>Container</ExternalCell>
            <ExternalCell startColumn={1} textTransform="unset">
              CPU SURGE
              <InfoTooltip
                sx={{ maxWidth: 190 }}
                title="Significant increase in used resources since the last applied value"
                noDefaultIconStyling={false}
              />
            </ExternalCell>
            <ExternalCell startColumn={2} textTransform="unset">
              MEMORY SURGE
              <InfoTooltip
                sx={{ maxWidth: 190 }}
                title="Significant increase in used resources since the last applied value"
                noDefaultIconStyling={false}
              />
            </ExternalCell>
          </ExternalRow>
          {event.details.containers.map(
            ({ name, surge: { cpuCores, memoryGib }, recommendedBefore }) => (
              <ExternalRow key={name} height={28}>
                <ExternalCell startColumn={0}>{name}</ExternalCell>
                <ExternalCell
                  cellProps={{ display: 'flex', alignItems: 'center' }}
                  startColumn={1}
                >
                  {renderSurge(recommendedBefore?.cpuCores, cpuCores, 'CPU')}
                </ExternalCell>
                <ExternalCell
                  cellProps={{ display: 'flex', alignItems: 'center' }}
                  startColumn={2}
                >
                  {renderSurge(recommendedBefore?.memoryGib, memoryGib, 'GiB')}
                </ExternalCell>
              </ExternalRow>
            )
          )}
        </>
      )}
    </DetailsRowContainer>
  );
};

const rowByType: Partial<Record<WorkloadEventType, (props: any) => ReactNode>> =
  {
    [WorkloadEventType.OOM_KILL]: OomKill,
    [WorkloadEventType.SURGE]: Surge,
    [WorkloadEventType.FAILED_HELM_TEST_HOOK]: FailedHelmTestEventSubRow,
    [WorkloadEventType.CONFIGURATION_CHANGEDV2]: (event) => (
      <ConfigurationEventSubRow event={event} />
    ),
    [WorkloadEventType.RECOMMENDED_POD_COUNT_CHANGED]:
      RecommendedPodCountChanged,
    [WorkloadEventType.RECOMMENDED_REQUESTS_CHANGED]: RecommendationEvent,
    [WorkloadEventType.SCALING_POLICY_CREATED]: (event) => (
      <ScalingPolicyEventDetailsRowContainer event={event} />
    ),
    [WorkloadEventType.SCALING_POLICY_UPDATED]: (event) => (
      <ScalingPolicyUpdatedEventSubRow event={event} />
    ),
    [WorkloadEventType.SCALING_POLICY_DELETED]: (event) => (
      <ScalingPolicyEventDetailsRowContainer event={event} />
    ),
    [WorkloadEventType.SCALING_POLICY_ASSIGNED]: (event) => (
      <ScalingPolicyEventDetailsRowContainer event={event} />
    ),
    [WorkloadEventType.SCALING_POLICY_ORDER_UPDATED]: (event) => (
      <ScalingPolicyOrderChangedEventDetailsRow event={event} />
    ),
    [WorkloadEventType.SYSTEM_OVERRIDE_RESET]: SystemOverrideResetEventRow,
    [WorkloadEventType.SYSTEM_OVERRIDE_TRIGGERED]:
      SystemOverrideTriggeredEventRow,
    [WorkloadEventType.STARTUP_FAILURE]: StartupFailureEventRow,
    [WorkloadEventType.CPU_PRESSURE]: CPUPressureEventSubRow,
    [WorkloadEventType.CPU_PRESSURE_RESOLVED]: CPUPressureResolvedEventSubRow,
  };

export const EventDetailsSubRow = (event: PreparedEvent) => {
  const component = rowByType[event.type];
  // handle unknown events
  if (!component) {
    return <DetailsRowContainer event={event} jsonOnly />;
  }
  return component(event as any);
};
